import type { NetSession } from './session';
import { useGameStore } from '../store/gameSlice';
import {
  serializeSnapshot,
  serializeFloor,
  type FloorMessage,
} from './replication';
import type {
  ITile,
  IRoom,
  PlacedLocation,
  MapStyle,
} from '../types/map.types';
import { dungeonService } from '../services/DungeonService';

const SNAPSHOT_INTERVAL_MS = 50; // 20 Hz

export class ReplicationHost {
  private lastSent = 0;
  private seq = 0;
  private lastFloor: FloorMessage | null = null;

  constructor(
    private session: NetSession,
    private now: () => number = () => performance.now(),
  ) {}

  private captureCommonItems(): void {
    if (!this.lastFloor) {
      return;
    }
    this.lastFloor.commonItems = dungeonService.inventoryService
      .getAllGroundItems()
      .filter((gi) => gi.mode === 'common')
      .map((gi) => ({
        id: gi.id,
        itemId: gi.itemId,
        x: gi.x,
        y: gi.y,
        quantity: gi.quantity,
        dropperId: gi.dropperId,
      }));
  }

  /** Snapshot the current location consumption onto the cached floor so a
   *  resend restores a (re)joining client's prompts (cf. FloorMessage). */
  private captureLocationConsumption(): void {
    if (!this.lastFloor) {
      return;
    }
    const loc = dungeonService.locationService;
    this.lastFloor.usedLocations = loc.getUsedLocationKeys();
    this.lastFloor.destroyedLocations = loc.getGlobalDestroyedKeys();
    this.lastFloor.usedByPlayer = loc.getUsedByPlayer();
  }

  sendFloor(
    tiles: ITile[],
    width: number,
    height: number,
    tilesetKey: string,
    biomeId: string,
    localPlayerId: string,
    placedLocations: PlacedLocation[],
    rooms: IRoom[],
    style: MapStyle,
  ): void {
    this.lastFloor = serializeFloor(
      tiles,
      width,
      height,
      tilesetKey,
      biomeId,
      localPlayerId,
      placedLocations,
      rooms,
      style,
    );
    this.lastFloor.floor = useGameStore.getState().currentFloor;
    this.captureCommonItems();
    this.captureLocationConsumption();
    this.session.broadcast('reliable', this.lastFloor);
  }

  /** Re-broadcast the current floor to a late-joining peer. */
  resendFloor(): void {
    if (this.lastFloor) {
      this.captureCommonItems();
      this.captureLocationConsumption();
      this.session.broadcast('reliable', this.lastFloor);
    }
  }

  /** Call every game-loop tick; emits a snapshot at the fixed network rate. */
  tick(): void {
    const t = this.now();
    if (t - this.lastSent < SNAPSHOT_INTERVAL_MS) {
      return;
    }
    this.lastSent = t;
    this.seq += 1;
    this.session.broadcast('unreliable', serializeSnapshot(t, this.seq));
  }
}
